import React, { Suspense, StrictMode } from 'react';
import 'regenerator-runtime/runtime';

import '../../../init';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';

import { isRedirectingToLogin } from '@/api/auth-state';
import { retry, retryDelay } from '@/api/retryPolicy';
import AsciiArt from '@/components/atoms/AsciiArt';
import Loading from '@/components/atoms/Loading/Loading';
import ApolloProvider from '@/components/templates/ApolloProvider';
import { CacheBootstrap } from '@/components/templates/CacheBootstrap';
import { browserRouter } from '@/components/templates/Routes/RootRouter';
import RtmProvider from '@/components/templates/RtmProvider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { createMutationCache } from '@/lib/mutationCache';
import { registeredCaches } from '@/lib/storage/cacheRegistry';
import { getStore } from '@/redux/store';

import '@/styles/globals.css';

const store = getStore();

/**
 * Global query cache with error handling to prevent infinite refetch loops.
 * The queryClient variable is declared before the cache since onError only runs after initialization.
 */
// eslint-disable-next-line prefer-const
let queryClient: QueryClient;

const queryCache = new QueryCache({
  onError: () => {
    // 401 → redirect is handled centrally in fetcher.ts/client.ts via
    // handleAuthFailure. By the time this onError fires, navigation has
    // already started. Cancel in-flight queries so they don't continue
    // wastefully completing during the brief window before page unload.
    // We don't `clear()` the cache: it'd force every mounted component
    // into a loading state if navigation doesn't actually fire (10s
    // safety reset), and the cache goes away with the page anyway.
    if (isRedirectingToLogin) {
      queryClient.cancelQueries();
    }
    // Note: 404 errors should NOT remove queries from cache.
    // Doing so causes infinite re-fetch loops because:
    // 1. Query fails with 404 → removed from cache (loses error state)
    // 2. Component re-renders → query re-triggered (enabled still true)
    // 3. Loop continues, component stuck in 'pending' state forever
    // Instead, let the error state persist so components can render error UI.
  },
});

queryClient = new QueryClient({
  queryCache,
  mutationCache: createMutationCache(),
  defaultOptions: {
    queries: {
      notifyOnChangeProps: ['data', 'error'],
      staleTime: 60000 /* 1 MINUTE */,
      refetchOnWindowFocus: true,
      retry,
      retryDelay,
    },
    mutations: { retry, retryDelay },
  },
});

if (process.env.NODE_ENV === 'production') {
  // eslint-disable-next-line no-console
  console.log(AsciiArt);
}

const App: React.FC = () => (
  <StrictMode>
    <TooltipProvider>
      <Suspense fallback={<Loading />}>
        <Provider store={store}>
          <CacheBootstrap caches={registeredCaches} />
          <QueryClientProvider client={queryClient}>
            <ApolloProvider>
              <RtmProvider>
                <DndProvider backend={HTML5Backend}>
                  <RouterProvider router={browserRouter} />

                  {process.env.NODE_ENV !== 'test' && <ReactQueryDevtools initialIsOpen={false} />}
                </DndProvider>
                <Toaster expand />
              </RtmProvider>
            </ApolloProvider>
          </QueryClientProvider>
        </Provider>
      </Suspense>
    </TooltipProvider>
  </StrictMode>
);

export default App;
